import React, { useState } from 'react';
import axios from 'axios';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const ChangePasswordModal = ({ isOpen, onClose, onSuccess }) => {
	const [formData, setFormData] = useState({
		password_actual: '',
		password_nueva: '',
		password_nueva_confirm: ''
	});
	const [showPasswords, setShowPasswords] = useState({
		actual: false,
		nueva: false,
		confirm: false
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value
		});
		setError('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setLoading(true);

		// Validaciones básicas
		if (!formData.password_actual) {
			setError('Debe ingresar su contraseña actual.');
			setLoading(false);
			return;
		}

		if (!formData.password_nueva) {
			setError('Debe ingresar una nueva contraseña.');
			setLoading(false);
			return;
		}

		if (formData.password_nueva.length < 8) {
			setError('La nueva contraseña debe tener al menos 8 caracteres.');
			setLoading(false);
			return;
		}

		if (formData.password_nueva !== formData.password_nueva_confirm) {
			setError('Las contraseñas nuevas no coinciden.');
			setLoading(false);
			return;
		}

		try {
			const token = localStorage.getItem('authToken');
			const response = await axios.post(
				`${API_URL}/api/auth/cambiar-password/`,
				formData,
				{
					headers: {
						'Authorization': `Token ${token}`
					}
				}
			);

			if (response.status === 200) {
				// Limpiar formulario
				setFormData({
					password_actual: '',
					password_nueva: '',
					password_nueva_confirm: ''
				});
				onSuccess();
			}
		} catch (error) {
			console.error('Error cambiando contraseña:', error);
			const errorMessage = error.response?.data?.error || 
			                    error.response?.data?.detail ||
			                    'Error al cambiar la contraseña. Intenta nuevamente.';
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
			<div className="bg-white rounded-lg max-w-md w-full shadow-xl">
				<div className="p-6">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-2xl font-bold text-gray-900">
							Cambiar Contraseña
						</h2>
						{/* No permitir cerrar el modal hasta cambiar la contraseña */}
					</div>

					<p className="text-sm text-gray-600 mb-6">
						Es tu primer inicio de sesión. Por seguridad, debes cambiar tu contraseña antes de continuar.
					</p>

					{error && (
						<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					)}

					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label htmlFor="password_actual" className="block text-sm font-medium text-gray-700 mb-1">
								Contraseña Actual *
							</label>
							<div className="relative">
								<input
									id="password_actual"
									name="password_actual"
									type={showPasswords.actual ? 'text' : 'password'}
									required
									value={formData.password_actual}
									onChange={handleChange}
									className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
									disabled={loading}
								/>
								<button
									type="button"
									onClick={() => setShowPasswords({ ...showPasswords, actual: !showPasswords.actual })}
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPasswords.actual ? (
										<EyeSlashIcon className="h-5 w-5" />
									) : (
										<EyeIcon className="h-5 w-5" />
									)}
								</button>
							</div>
						</div>

						<div>
							<label htmlFor="password_nueva" className="block text-sm font-medium text-gray-700 mb-1">
								Nueva Contraseña * (mínimo 8 caracteres)
							</label>
							<div className="relative">
								<input
									id="password_nueva"
									name="password_nueva"
									type={showPasswords.nueva ? 'text' : 'password'}
									required
									minLength={8}
									value={formData.password_nueva}
									onChange={handleChange}
									className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
									disabled={loading}
								/>
								<button
									type="button"
									onClick={() => setShowPasswords({ ...showPasswords, nueva: !showPasswords.nueva })}
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPasswords.nueva ? (
										<EyeSlashIcon className="h-5 w-5" />
									) : (
										<EyeIcon className="h-5 w-5" />
									)}
								</button>
							</div>
						</div>

						<div>
							<label htmlFor="password_nueva_confirm" className="block text-sm font-medium text-gray-700 mb-1">
								Confirmar Nueva Contraseña *
							</label>
							<div className="relative">
								<input
									id="password_nueva_confirm"
									name="password_nueva_confirm"
									type={showPasswords.confirm ? 'text' : 'password'}
									required
									minLength={8}
									value={formData.password_nueva_confirm}
									onChange={handleChange}
									className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
									disabled={loading}
								/>
								<button
									type="button"
									onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPasswords.confirm ? (
										<EyeSlashIcon className="h-5 w-5" />
									) : (
										<EyeIcon className="h-5 w-5" />
									)}
								</button>
							</div>
						</div>

						<div className="flex gap-3 pt-4">
							<button
								type="submit"
								disabled={loading}
								className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							>
								{loading ? 'Cambiando...' : 'Cambiar Contraseña'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default ChangePasswordModal;

